<template>
  <div>
    <label
      v-if="label"
      :for="name"
      class="text-background-fg/70 mb-1 block text-left text-xs font-medium"
    >
      {{ label }}
    </label>
    <ul class="flex flex-wrap gap-1">
      <li v-for="tagId in inputValue" :key="tagId">
        <ProductTagChip
          :product-tag-id="tagId"
          removable
          @click-remove="removeTag(tagId)"
        />
      </li>
      <button
        type="button"
        class="text-background-fg/70 inline-flex items-center justify-center rounded-full text-xs"
        @click="onClickAddTag"
      >
        <IconHeroiconsSolidPlus
          class="aspect-square h-4 w-4"
        />
      </button>
    </ul>
  </div>

  <BaseDialog
    :is-open="isAddDialogOpen"
    @close="hideAddDialog"
  >
    <div class="p-4">
      <template v-if="isCreatingTag">
        <ProductTagForm
          :is-submitting="isSubmitting"
          @submit="onCreateTag"
          @cancel="isCreatingTag = false"
        />
      </template>
      <template v-else-if="editingTag !== undefined">
        <ProductTagForm
          :is-submitting="isSubmitting"
          :initial-values="editingTag"
          @submit="
            (params) =>
              editingTag && onEditTag(editingTag.id, params)
          "
          @cancel="editingTag = undefined"
        />
      </template>
      <template v-else>
        <div class="space-y-4">
          <div>
            <div
              class="mb-2 flex items-center justify-between"
            >
              <h2
                class="text-background-fg/87 mb-2 text-left"
              >
                Active Tags
              </h2>
            </div>
            <ul class="border-1 border-background-fg/8">
              <template v-if="inputValue.length > 0">
                <template
                  v-for="productTagId in inputValue"
                  :key="productTagId"
                >
                  <li
                    class="bg-background flex items-center justify-between space-x-2 px-2 py-1"
                  >
                    <ProductTagChip
                      :product-tag-id="productTagId"
                    />
                    <div class="flex-1" />
                    <div class="flex items-center">
                      <button
                        type="button"
                        class="border-1 block flex w-full items-center justify-end rounded-lg px-1 text-xs"
                        @click="
                          onStartEditTagById(productTagId)
                        "
                      >
                        Edit
                      </button>
                    </div>

                    <button
                      class="flex items-center justify-center"
                      @click="removeTag(productTagId)"
                    >
                      <IconHeroiconsSolidMinus />
                    </button>
                  </li>
                </template>
              </template>
              <template v-else>
                <p
                  class="text-background-fg/87 py-1 text-sm"
                >
                  No active tags
                </p>
              </template>
            </ul>
          </div>
          <div>
            <div
              class="mb-2 flex items-center justify-between"
            >
              <h2 class="text-background-fg/87text-left">
                Available Tags
              </h2>
              <ButtonCreate @click="onStartCreateTag">
                Create Tag
              </ButtonCreate>
            </div>
            <ul class="border-1 border-background-fg/8">
              <template
                v-for="productTag in availableTags"
                :key="productTag.id"
              >
                <li
                  class="flex items-center justify-between space-x-2 px-2 py-1"
                  :class="'bg-background'"
                >
                  <ProductTagChip
                    :product-tag-id="productTag.id"
                  />
                  <div class="flex-1" />
                  <div class="flex items-center">
                    <button
                      type="button"
                      class="border-1 block flex w-full items-center justify-end rounded-lg px-1 text-xs"
                      @click="onStartEditTag(productTag)"
                    >
                      Edit
                    </button>
                  </div>

                  <button
                    class="flex items-center justify-center"
                    @click="addTag(productTag.id)"
                  >
                    <IconHeroiconsSolidPlus />
                  </button>
                </li>
              </template>
            </ul>
          </div>
        </div>
      </template>
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
import { useField } from "vee-validate"
import type { ProductTagIn } from "@/services/product-tags/createProductTag"
import type { ProductTag } from "@/services/product-tags/listProductTags"
const props = defineProps<{
  name: string
  label: string
}>()

const { value: inputValue, setValue } = useField<string[]>(
  props.name,
  undefined,
  {
    initialValue: [],
  }
)

const { data: productTags, getById } = useProductTags()

const availableTags = computed(
  () =>
    productTags.value?.filter(
      (tag) => !inputValue.value.includes(tag.id)
    ) ?? []
)

const addTag = (tagId: string) => {
  setValue([
    ...new Set([...(inputValue.value || []), tagId]),
  ])
}
const removeTag = (idToRemove: string) => {
  setValue(
    inputValue.value?.filter((id) => id !== idToRemove)
  )
}

const isAddDialogOpen = ref(false)
const onClickAddTag = () => {
  isCreatingTag.value = false
  editingTag.value = undefined
  isAddDialogOpen.value = true
}
const hideAddDialog = () => {
  isAddDialogOpen.value = false
}

const editingTag = ref<ProductTag | undefined>(undefined)
const onStartEditTag = (productTag: ProductTag) => {
  editingTag.value = productTag
}
const onStartEditTagById = (
  productTagId: ProductTag["id"]
) => {
  const productTag = getById(productTagId).value
  if (!productTag) return
  onStartEditTag(productTag)
}
const isCreatingTag = ref(false)
const onStartCreateTag = () => {
  isCreatingTag.value = true
}

const isSubmitting = ref(false)
const onCreateTag = async (params: ProductTagIn) => {
  isSubmitting.value = true
  try {
    await createProductTag(params)
    isCreatingTag.value = false
  } catch {
    toast.error("Failed to create product tag")
  } finally {
    isSubmitting.value = false
  }
}

const onEditTag = async (
  productTagId: string,
  params: ProductTagIn
) => {
  isSubmitting.value = true
  try {
    await updateProductTag(productTagId, params)
    editingTag.value = undefined
  } catch {
    toast.error("Failed to edit product tag")
  } finally {
    isSubmitting.value = false
  }
}
</script>
